<template>
  <div class="qec-wrap">
    <canvas ref="canvas" class="qec-canvas"></canvas>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { markRaw } from "vue";

export default {
  name: "QuantyEvolucionChart",
  props: {
    id:           { type: String, required: true },
    labels:       { type: Array, default: () => [] },
    ventasNuevas: { type: Array, default: () => [] },
    ventasRenov:  { type: Array, default: () => [] },
    importeEur:   { type: Array, default: () => [] },
    importeUsd:   { type: Array, default: () => [] },
  },
  data() {
    return { chart: null };
  },
  mounted() {
    this.$nextTick(() => requestAnimationFrame(() => this.initChart()));
  },
  beforeUnmount() {
    this.destroy();
  },
  watch: {
    labels:       'syncChart',
    ventasNuevas: 'syncChart',
    ventasRenov:  'syncChart',
    importeEur:   'syncChart',
    importeUsd:   'syncChart',
  },
  methods: {
    syncChart() {
      if (!this.chart) return;
      this.chart.data.labels = [...this.labels];
      this.chart.data.datasets[0].data = [...this.ventasNuevas];
      this.chart.data.datasets[1].data = [...this.ventasRenov];
      this.chart.data.datasets[2].data = [...this.importeEur];
      this.chart.data.datasets[3].data = [...this.importeUsd];
      this.chart.update();
    },
    destroy() {
      if (this.chart) { this.chart.destroy(); this.chart = null; }
    },
    fmtEur(n) {
      return Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
    },
    fmtUsd(n) {
      return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
    },
    initChart() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      this.destroy();

      const ctx = canvas.getContext("2d");

      const gradNuevas = ctx.createLinearGradient(0, 0, 0, 260);
      gradNuevas.addColorStop(0, "rgba(30,136,229,0.85)");
      gradNuevas.addColorStop(1, "rgba(0,188,212,0.60)");

      const gradRenov = ctx.createLinearGradient(0, 0, 0, 260);
      gradRenov.addColorStop(0, "rgba(124,77,255,0.80)");
      gradRenov.addColorStop(1, "rgba(94,53,177,0.55)");

      this.chart = markRaw(new Chart(canvas, {
        type: "bar",
        data: {
          labels: [...this.labels],
          datasets: [
            {
              type: "bar",
              label: "Nuevas",
              data: [...this.ventasNuevas],
              yAxisID: "yV",
              backgroundColor: gradNuevas,
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: 44,
              stack: "ventas",
              order: 3,
            },
            {
              type: "bar",
              label: "Renov.",
              data: [...this.ventasRenov],
              yAxisID: "yV",
              backgroundColor: gradRenov,
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: 44,
              stack: "ventas",
              order: 4,
            },
            {
              type: "line",
              label: "EUR €",
              data: [...this.importeEur],
              yAxisID: "yI",
              borderColor: "#ff9800",
              backgroundColor: "rgba(255,152,0,0.08)",
              fill: true,
              tension: 0.38,
              pointRadius: 5,
              pointHoverRadius: 7,
              pointBackgroundColor: "#ff9800",
              borderWidth: 2.5,
              order: 1,
            },
            {
              type: "line",
              label: "USD $",
              data: [...this.importeUsd],
              yAxisID: "yI",
              borderColor: "#26c6da",
              backgroundColor: "rgba(38,198,218,0.07)",
              fill: true,
              tension: 0.38,
              pointRadius: 5,
              pointHoverRadius: 7,
              pointBackgroundColor: "#26c6da",
              borderWidth: 2.5,
              order: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: {
              display: true,
              position: "top",
              align: "end",
              labels: {
                boxWidth: 12,
                boxHeight: 12,
                borderRadius: 4,
                useBorderRadius: true,
                font: { size: 12, family: "Inter" },
                color: "#e7edf5",
              },
            },
            tooltip: {
              backgroundColor: "rgba(14,20,31,0.96)",
              titleFont: { size: 12, family: "Inter" },
              bodyFont:  { size: 12, family: "Inter" },
              padding: 10,
              callbacks: {
                label: (ctx) => {
                  if (ctx.datasetIndex === 0) return `  Nuevas: ${ctx.raw}`;
                  if (ctx.datasetIndex === 1) return `  Renov.: ${ctx.raw}`;
                  if (ctx.datasetIndex === 2) return `  EUR: ${this.fmtEur(ctx.raw)}`;
                  return `  USD: ${this.fmtUsd(ctx.raw)}`;
                },
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false, drawBorder: false },
              ticks: { color: "#7b8ea8", font: { size: 11, family: "Inter" } },
            },
            yV: {
              type: "linear",
              position: "left",
              stacked: true,
              grid: { color: "rgba(255,255,255,0.05)", drawBorder: false },
              ticks: {
                stepSize: 1,
                color: "#5c9eff",
                font: { size: 11, family: "Inter" },
                callback: (v) => Number.isInteger(v) ? v : null,
              },
              title: {
                display: true,
                text: "Ventas",
                color: "#5c9eff",
                font: { size: 11, family: "Inter" },
              },
            },
            yI: {
              type: "linear",
              position: "right",
              grid: { display: false, drawBorder: false },
              ticks: {
                color: "#ff9800",
                font: { size: 11, family: "Inter" },
                callback: (v) => this.fmtEur(v),
              },
              title: {
                display: true,
                text: "Ingresos",
                color: "#ff9800",
                font: { size: 11, family: "Inter" },
              },
            },
          },
        },
      }));
    },
  },
};
</script>

<style scoped>
.qec-wrap  { position: relative; width: 100%; height: 300px; }
.qec-canvas{ width: 100% !important; height: 100% !important; }
</style>
